import type { NodeApi } from '../util/detectApi';
import type { PreferredFileOps, FilesystemApis } from '../upload/fileOps';
import { recursiveMkdir } from '../upload/fileOps';
import { dirname, open, read, write, mkdir, close, copyFile, rename, doesPathExist, join, normalize } from '../util/fs';
import type { Logger } from '../types/logger';

/**
 * Consider using interface Vfs and module localFs.ts instead when writing new
 * code.
 *
 * The NodeFileOps is mostly a file system abstraction for the Mimir UI.
 *
 * Most of the functions are just routed through the wrappers in
 * common/src/util/fs.ts, since those already take care of some special logics
 * regarding Premiere and Electron. Those use the same getNodeApi which was also
 * used to pass the node API to NodeFileOps - which is why it was removed from
 * here since it became unused. If a dependency injection like that becomes
 * necessary (e.g. for unit tests), we can further improve NodeFileOps to make
 * it the common base for the functions in common/src/util/fs.ts.
 */
export class NodeFileOps implements PreferredFileOps {

  constructor(private logger: Logger, private nodeApi: NodeApi) {}

  mkdir(path: string): Promise<void> {
    return mkdir(this.nodeApi, path);
  }
  dirname(path: string): string {
    return dirname(this.nodeApi, path);
  }
  open(path: string, flags: 'r' | 'w'): Promise<number> {
    return open(this.nodeApi, path, flags, 0o666);
  }
  close(fd: number): Promise<void> {
    return close(this.nodeApi, fd);
  }
  read(fd: number, data: Uint8Array, offset: number, length: number): Promise<{ bytesRead: number, buffer: Uint8Array }> {
    return read(this.nodeApi, fd, data, offset, length, null);
  }
  write(fd: number, buffer: Uint8Array, offset: number, length: number): Promise<{ bytesWritten: number, buffer: Uint8Array }> {
    return write(this.nodeApi, fd, buffer, offset, length, null);
  }

  copyFileCore(src: string, dest: string): Promise<void> {
    return copyFile(this.nodeApi, src, dest);
  }

  rename(oldPath: string, newPath: string): Promise<void> {
    return rename(this.nodeApi, oldPath, newPath);
  }

  async copyFile(src: string, dest: string): Promise<void> {
    /*
      TODO: We copy then rename to mimic the strategy used by Minio. However, if
      we want to add growing file support we may want to change this later.
    */
    const tmppath = dest + '.part';
    await recursiveMkdir(this, dirname(this.nodeApi, dest));
    await this.copyFileCore(src, tmppath);
    return this.rename(tmppath, dest);
  }

  join(...paths: string[]): string {
    return join(this.nodeApi, ...paths);
  }
  isDirectory(path: string): Promise<boolean> {
    return doesPathExist(this.nodeApi, this.logger, path, true);
  }
  normalize(path: string): string {
    return normalize(this.nodeApi, path);
  }
}

export function createNodeFsApis(logger: Logger, nodeApi: NodeApi, canUseBlob: boolean): FilesystemApis {
  return {
    preferred: new NodeFileOps(logger, nodeApi),
    canUseBlob,
  };
}

